import {
  Accordion,
  AccordionSummary,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  FormGroup,
  Checkbox as MuiCheckbox,
  Icon as MuiIcon,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  DigitalTwinSettingCreationRequest,
  setSettingRequestMinutes,
  splitTimeSeriesSettingOnNullValues,
  TimeSeriesSettingCreationRequest,
} from 'api/digitalTwin/digitalTwin.api'
import {
  useApproveEventChangeRequestMutation,
  useCreateEventChangeRequestMutation,
  useDeleteEventChangeRequestMutation,
  useGetEventConfig,
  useRejectEventChangeRequestMutation,
  useUpdateEventChangeRequestMutation,
} from 'api/events/events.api'
import classNames from 'classnames'
import Delta from 'quill-delta'
import React, { ReactElement, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactQuill from 'react-quill-new'
import alertStore from 'store/alert/alert'
import { ALERT_KEYS } from 'store/alert/alert.constants'
import { colors } from 'styles/variables'
import { Button } from 'ui/atoms'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import Dropdown from 'ui/components/Dropdown/Dropdown'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import SearchableDropdown from 'ui/components/SearchableDropdown/SearchableDropdown'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import { DatePickerPeriod } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'
import Datetime from 'utils/datetime/datetime'
import styles from '../EventPostModal/EventPostModal.module.less'
import EventPropertiesDropdownsSettingInstructions from '../EventPropertiesDropdowns/EventPropertiesDropdownsSettingInstructions'
import ChangeRequestSettingsInstructionsOverviewTable, {
  SettingsInstructionsData,
} from '../EventSettingsOverviewTable/ChangeRequestSettingInstructionOverviewTable'
import {
  checkIfEditing,
  createdAndUpdatedInfoChangeRequest,
  EMPTY_LINKED_OBJECT_ATTRIBUTE,
  EVENT_TYPES,
  EventChangeRequest,
  EventPost,
  generateUniqueId,
  getEventPostForSchedulabilityChange,
  getNumberOfSettingInstructions,
  hasDuplicateLinkedObjects,
  hasOversizedEmbeddedImage,
  hasSettingsOutsideTimePeriod,
  type InputFieldDeltas,
  initializeSettingInstructionsWithIds,
  isEquivalentEditorContent,
  MAX_IMAGE_SIZE_MB,
  MAX_SCHEDULABILITY_EFFECT_INPUT_LENGTH,
  normalizeLinkedObjectAttribute,
  noSettingWritePermissionSettingInstructions,
  PermittedActionsChangeRequest,
  reactQuillModules,
  SCHEDULABILITIES,
  SettingOrTimeSeriesInstructions,
  TimeSeriesSettingInstruction,
  useInputFieldEditorContent,
} from '../events.helper'

type EventEditProposalModalProps = {
  eventPost: EventPost
  mode: 'new' | 'edit' | 'read-only'
  closeModal: () => void
  onEventUpdated?: (updatedEventPost: EventPost) => void
}

type EventChangeRequestUiState = {
  handInChangeRequest: boolean
  showCommentModal: boolean
  changeRequestComment: string
  schedulabilityEffectLength: number
  editorErrorMessage: string
  openSettingsOverview: { [index: number]: boolean }
  confirmCloseChangeRequest: boolean
  confirmThrowAwayChangeRequest: boolean
  confirmDeleteOwnChangeRequest: boolean
  confirmRejectChangeRequest: boolean
  confirmApproveChangeRequest: boolean
  confirmOnRemoveAffectedComponent: boolean
  selectedAffectedComponent: number
}

function eventChangeRequestUiReducer(
  state: EventChangeRequestUiState,
  update: Partial<EventChangeRequestUiState>
): EventChangeRequestUiState {
  return { ...state, ...update }
}

const EmptyElement = React.memo(function EmptyElement(): ReactElement {
  return <></>
})

export default function EventChangeRequestModal({
  eventPost,
  mode,
  closeModal,
  onEventUpdated,
}: EventEditProposalModalProps): ReactElement {
  return useEventChangeRequestModalView({ eventPost, mode, closeModal, onEventUpdated })
}

function useEventChangeRequestModalView({
  eventPost,
  mode,
  closeModal,
  onEventUpdated,
}: EventEditProposalModalProps): ReactElement {
  const createNormalizedChangeRequest = useCallback(
    (request: EventChangeRequest): EventChangeRequest => ({
      ...JSON.parse(JSON.stringify(request)),
      setting_instructions: initializeSettingInstructionsWithIds(request.setting_instructions),
    }),
    []
  )

  const initialChangeRequestPost = useMemo<EventChangeRequest>(
    () => ({
      operational_event_id: eventPost.id,
      comment: '',
      request_status: 'Pending',
      name: eventPost.name ?? '',
      input_field: eventPost.input_field ?? {},
      operational_event_type: eventPost.operational_event_type,
      schedulability: eventPost.schedulability,
      schedulability_effect: eventPost.schedulability_effect ?? undefined,
      start_time: eventPost.start_time ?? '',
      end_time: eventPost.end_time ?? '',
      earliest_start_time: eventPost.earliest_start_time ?? undefined,
      latest_end_time: eventPost.latest_end_time ?? undefined,
      category_id: eventPost.category_id,
      require_umm: eventPost.require_umm ?? false,
      cancelable_after_start: eventPost.cancelable_after_start ?? false,
      status: eventPost.status,
      system_id: eventPost.system_id,
      digital_twin_id: eventPost.digital_twin_id,
      setting_instructions: { linked_objects: eventPost.setting_instructions?.linked_objects ?? [] },
      permitted_actions: ['update'],
    }),
    [eventPost]
  )
  const { t } = useTranslation()

  const [manualModalMode, setModalMode] = useState<'new' | 'edit' | 'read-only' | null>(null)
  const modalMode = manualModalMode ?? mode

  const [changeRequestPost, setChangeRequestPost] = useState(() => {
    const request =
      modalMode === 'new'
        ? initialChangeRequestPost
        : ((eventPost.change_requests && eventPost.change_requests[0]) ?? initialChangeRequestPost)

    return createNormalizedChangeRequest(request)
  })
  const [lastSavedValues, setLastSavedValuesState] = useState<EventChangeRequest>(() => {
    const existingChangeRequest = eventPost.change_requests?.[0]

    if (eventPost.pending_change_request_id && existingChangeRequest) {
      return createNormalizedChangeRequest(existingChangeRequest)
    }

    return createNormalizedChangeRequest(initialChangeRequestPost)
  })
  const [inputFieldDeltaState, setInputFieldDeltaState] = useState<{
    saved: Delta | null
    current: Delta | null
  }>({
    saved: null,
    current: null,
  })
  const [isQuillMounted, setIsQuillMounted] = useState(false)

  const setLastSavedValues = useCallback(
    (values: EventChangeRequest) => {
      setLastSavedValuesState(createNormalizedChangeRequest(values))
      setInputFieldDeltaState({ saved: null, current: null })
    },
    [createNormalizedChangeRequest]
  )

  function permittedTo(action: PermittedActionsChangeRequest): boolean {
    return changeRequestPost.permitted_actions ? changeRequestPost.permitted_actions.includes(action) : false
  }

  const [uiState, updateUiState] = useReducer(eventChangeRequestUiReducer, {
    handInChangeRequest: false,
    showCommentModal: false,
    changeRequestComment: changeRequestPost.comment ?? '',
    schedulabilityEffectLength: eventPost?.schedulability_effect?.length || 0,
    editorErrorMessage: '',
    openSettingsOverview: {},
    confirmCloseChangeRequest: false,
    confirmThrowAwayChangeRequest: false,
    confirmDeleteOwnChangeRequest: false,
    confirmRejectChangeRequest: false,
    confirmApproveChangeRequest: false,
    confirmOnRemoveAffectedComponent: false,
    selectedAffectedComponent: -1,
  })

  const { systemId, hasAccess } = useAuth()
  const hasAccessToUMMCheckbox = hasAccess({ submodule: 'operational_event_umm', module: 'events' })

  const linkedObjectFromInstructions = changeRequestPost?.setting_instructions?.linked_objects

  const settingInstructions: SettingOrTimeSeriesInstructions[] = useMemo(() => {
    const instructions: SettingOrTimeSeriesInstructions[] = []
    linkedObjectFromInstructions?.forEach((linkedObject) => {
      if (linkedObject.time_series_setting_instructions) {
        instructions.push(...linkedObject.time_series_setting_instructions)
      }
      if (linkedObject.setting_instructions) {
        instructions.push(...linkedObject.setting_instructions)
      }
    })

    return instructions
  }, [linkedObjectFromInstructions])

  const quillRef = useRef<ReactQuill | null>(null)
  const lastSeededSavedValues = useRef<EventChangeRequest | null>(null)
  const eventConfig = useGetEventConfig(systemId).data
  const eventCategories = eventConfig?.categories

  const inputFieldEditorContent = useInputFieldEditorContent(changeRequestPost, eventConfig)

  const disabledSameLinkedObjects = hasDuplicateLinkedObjects(linkedObjectFromInstructions)

  const createdAndUpdatedInfo = createdAndUpdatedInfoChangeRequest(changeRequestPost, t)

  const { mutateAsync: createEventChangeRequest } = useCreateEventChangeRequestMutation()
  const { mutateAsync: updateEventChangeRequest } = useUpdateEventChangeRequestMutation()
  const { mutateAsync: approveEventChangeRequest } = useApproveEventChangeRequestMutation()
  const { mutateAsync: rejectEventChangeRequest } = useRejectEventChangeRequestMutation()
  const { mutateAsync: deleteEventChangeRequest } = useDeleteEventChangeRequestMutation()

  const sendChangeRequest = useCallback(
    async (data: EventChangeRequest, event_id: number) => {
      try {
        const response = await createEventChangeRequest({ data, event_id })

        if (response) {
          const responseWithIds = {
            ...response,
            setting_instructions: initializeSettingInstructionsWithIds(response.setting_instructions),
          }
          setChangeRequestPost(responseWithIds)
          setLastSavedValues(responseWithIds)

          if (onEventUpdated) {
            const updatedEventPost = {
              ...eventPost,
              change_requests: [responseWithIds],
              pending_change_request_id: response.id,
            }
            onEventUpdated(updatedEventPost)
          }
        }

        setModalMode('read-only')
        updateUiState({ handInChangeRequest: false })

        alertStore.success(t('Change request submitted successfully'))
      } catch (_) {
        alertStore.error(t('Failed to submit change request'))
      }
    },
    [t, createEventChangeRequest, onEventUpdated, eventPost, setLastSavedValues]
  )

  const updateChangeRequest = useCallback(
    async (data: EventChangeRequest, event_id: number, change_request_id: number) => {
      try {
        const updatedData = { ...data, system_id: eventPost.system_id }
        const response = await updateEventChangeRequest({ data: updatedData, event_id, change_request_id })
        if (response) {
          const responseWithIds = {
            ...response,
            setting_instructions: initializeSettingInstructionsWithIds(response.setting_instructions),
          }
          setChangeRequestPost(responseWithIds)
          setLastSavedValues(responseWithIds)

          if (onEventUpdated) {
            const updatedEventPost = {
              ...eventPost,
              change_requests: [responseWithIds],
              pending_change_request_id: response.id,
            }
            onEventUpdated(updatedEventPost)
          }
        }

        setModalMode('read-only')
        updateUiState({ handInChangeRequest: false })

        alertStore.success(t('Change request updated successfully'))
      } catch (_) {
        alertStore.error(t('Failed to update change request'))
      }
    },
    [t, updateEventChangeRequest, onEventUpdated, eventPost, setLastSavedValues]
  )

  const approveChangeRequest = useCallback(
    async (event_id: number, change_request_id: number) => {
      try {
        await approveEventChangeRequest({ event_id, change_request_id })
        alertStore.success(t('Change request approved successfully'), undefined, ALERT_KEYS.eventChangeRequestApproved)
      } catch (_) {
        alertStore.error(t('Failed to approve change request'))
      }
    },
    [t, approveEventChangeRequest]
  )

  const rejectChangeRequest = useCallback(
    async (event_id: number, change_request_id: number) => {
      try {
        await rejectEventChangeRequest({ event_id, change_request_id })
        alertStore.success(t('Change request rejected successfully'))
      } catch (_) {
        alertStore.error(t('Failed to reject change request'))
      }
    },
    [t, rejectEventChangeRequest]
  )

  const deleteChangeRequest = useCallback(
    async (event_id: number, change_request_id: number) => {
      try {
        await deleteEventChangeRequest({ event_id, change_request_id })
        alertStore.success(t('Change request deleted successfully'))
      } catch (_) {
        alertStore.error(t('Failed to delete change request'))
      }
    },
    [t, deleteEventChangeRequest]
  )

  useEffect(() => {
    setIsQuillMounted(false)

    const timeoutId = window.setTimeout(() => {
      setIsQuillMounted(true)
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [])

  useEffect(() => {
    if (!isQuillMounted) return

    if (lastSeededSavedValues.current === lastSavedValues) return

    lastSeededSavedValues.current = lastSavedValues

    const editor = quillRef.current?.getEditor?.()
    if (!editor?.getContents) return

    const delta = new Delta(editor.getContents().ops)
    queueMicrotask(() => {
      setInputFieldDeltaState({ saved: delta, current: delta })
    })
  }, [isQuillMounted, lastSavedValues])

  const inputFieldDeltas: InputFieldDeltas | undefined =
    inputFieldDeltaState.saved == null
      ? undefined
      : {
          saved: inputFieldDeltaState.saved,
          current: inputFieldDeltaState.current,
        }
  const hasChanges = checkIfEditing(changeRequestPost, lastSavedValues, eventConfig, inputFieldDeltas)
  const hasEmptyAffectedComponent = changeRequestPost.setting_instructions?.linked_objects.some(
    (linkedObject) => linkedObject.level === '' || linkedObject.name === ''
  )
  const hasEmptyFields = hasEmptyAffectedComponent || changeRequestPost.name === ''

  const handleEditorChange = useCallback(
    (content: string, source: string, editor: any) => {
      const editorContents = new Delta(editor.getContents().ops)

      if (source !== 'user') {
        if (lastSeededSavedValues.current !== lastSavedValues) {
          lastSeededSavedValues.current = lastSavedValues
          setInputFieldDeltaState({ saved: editorContents, current: editorContents })
        }

        return
      }

      setInputFieldDeltaState((previous) => ({ ...previous, current: editorContents }))

      if (hasOversizedEmbeddedImage(editorContents)) {
        updateUiState({ editorErrorMessage: `The image size exceeds the maximum limit of ${MAX_IMAGE_SIZE_MB}MB.` })
      } else {
        updateUiState({ editorErrorMessage: '' })
      }

      const currentEventType = changeRequestPost.operational_event_type
      if (!currentEventType) return

      const currentValue = changeRequestPost?.input_field?.[currentEventType] ?? ''
      if (isEquivalentEditorContent(content, currentValue, inputFieldEditorContent)) {
        return
      }

      const newChangeRequestPost = {
        ...changeRequestPost,
        input_field: {
          ...changeRequestPost.input_field,
          [currentEventType as string]: content,
        },
      }
      setChangeRequestPost(newChangeRequestPost)
    },
    [changeRequestPost, inputFieldEditorContent, lastSavedValues]
  )

  const toggleSettingOverview = useCallback(
    (index: number) => {
      updateUiState({
        openSettingsOverview: {
          ...uiState.openSettingsOverview,
          [index]: uiState.openSettingsOverview[index] === undefined ? true : !uiState.openSettingsOverview[index],
        },
      })
    },
    [uiState.openSettingsOverview]
  )

  const addEventPropertyField = useCallback((): void => {
    const updatedEventProperties = [
      ...(linkedObjectFromInstructions || []),
      {
        level: '',
        name: '',
        attribute: EMPTY_LINKED_OBJECT_ATTRIBUTE,
        setting_instructions: [],
        time_series_setting_instructions: [],
      },
    ]
    setChangeRequestPost((prevChangeRequestPost) => ({
      ...prevChangeRequestPost,
      setting_instructions: {
        ...prevChangeRequestPost.setting_instructions,
        linked_objects: updatedEventProperties,
      },
    }))
  }, [linkedObjectFromInstructions])

  const removeEventPropertyField = useCallback(
    (index: number) => {
      const updatedEventProperties = (linkedObjectFromInstructions || []).filter((_, i) => i !== index)
      const newChangeRequestPost = {
        ...changeRequestPost,
        setting_instructions: {
          ...changeRequestPost.setting_instructions,
          linked_objects: updatedEventProperties,
        },
      }
      setChangeRequestPost(newChangeRequestPost)
    },
    [changeRequestPost, linkedObjectFromInstructions]
  )

  const disableQuillEditor = modalMode === 'read-only'

  const updateSettingInstructions = useCallback((data: SettingsInstructionsData) => {
    setChangeRequestPost((previousChangeRequestPost) => {
      const updatingSetting = data.id !== undefined && data.id !== null && !data.delete

      const linkedObjectOfNewSetting = previousChangeRequestPost.setting_instructions?.linked_objects?.find(
        (linkedObject) =>
          linkedObject.level === data.level &&
          linkedObject.name === data.name &&
          linkedObject.attribute === data.attribute
      )

      if (!linkedObjectOfNewSetting) return previousChangeRequestPost
      const settingsAndTimeSeriesSettingsInstructionsForLinkedObject = [
        ...linkedObjectOfNewSetting.setting_instructions,
        ...linkedObjectOfNewSetting.time_series_setting_instructions,
      ]

      if (data.isTimeSeries) {
        //Delete, update and create for time series settings
        const timeSeriesSettingData: TimeSeriesSettingCreationRequest[] = splitTimeSeriesSettingOnNullValues(
          data as TimeSeriesSettingCreationRequest
        )
        let allTimeSeriesSettingInstructionsForLinkedObject = [
          ...linkedObjectOfNewSetting.time_series_setting_instructions,
        ]

        if (data.delete) {
          const indexOfDeletedSetting = allTimeSeriesSettingInstructionsForLinkedObject.findIndex(
            (setting) => setting.id === data.id
          )
          if (indexOfDeletedSetting !== -1) {
            allTimeSeriesSettingInstructionsForLinkedObject.splice(indexOfDeletedSetting, 1)
          }
        } else {
          const newSettings: TimeSeriesSettingInstruction[] = []
          timeSeriesSettingData.forEach((timeSeriesSetting) => {
            const times = Datetime.getHoursBetween(timeSeriesSetting.start_time, timeSeriesSetting.end_time)
            const value = times
              .map((time) => (time in timeSeriesSetting.value ? timeSeriesSetting.value[time].value : null))
              .filter((v): v is number => v !== null)

            const newId = generateUniqueId(
              newSettings.length,
              settingsAndTimeSeriesSettingsInstructionsForLinkedObject.concat(newSettings)
            )
            const newTimeSeriesSettingInstruction = {
              level: timeSeriesSetting.level || '',
              name: timeSeriesSetting.name || '',
              attribute: timeSeriesSetting.attribute || '',
              priority: timeSeriesSetting.priority || 0,
              comment: timeSeriesSetting.comment || '',
              start_time: timeSeriesSetting.start_time || undefined,
              end_time: timeSeriesSetting.end_time || undefined,
              values: value as number[],
              creator_name: timeSeriesSetting.creator_name || '',
              updated_at: Datetime.getISONow(0, false),
              id: newId, //always new id if it has split
            }

            newSettings.push(newTimeSeriesSettingInstruction)
          })

          if (updatingSetting) {
            const indexOfUpdatedSetting = allTimeSeriesSettingInstructionsForLinkedObject.findIndex(
              (setting) => setting.id === data.id
            )
            if (indexOfUpdatedSetting !== -1) {
              allTimeSeriesSettingInstructionsForLinkedObject.splice(indexOfUpdatedSetting, 1)
            }
          }
          allTimeSeriesSettingInstructionsForLinkedObject = [
            ...allTimeSeriesSettingInstructionsForLinkedObject,
            ...newSettings.map((setting) => ({
              ...setting,
            })),
          ]
        }
        return {
          ...previousChangeRequestPost,
          setting_instructions: {
            linked_objects: (previousChangeRequestPost.setting_instructions?.linked_objects ?? []).map(
              (linkedObject) => {
                if (
                  linkedObject.level === data.level &&
                  linkedObject.name === data.name &&
                  linkedObject.attribute === data.attribute
                ) {
                  return {
                    ...linkedObject,
                    time_series_setting_instructions: allTimeSeriesSettingInstructionsForLinkedObject,
                  }
                }
                return linkedObject
              }
            ),
          },
        }
      } else {
        //Delete, update and create for "normal" settings
        const settingsData = setSettingRequestMinutes(data) as DigitalTwinSettingCreationRequest

        let allSettingInstructionsForLinkedObject = [...linkedObjectOfNewSetting.setting_instructions]

        if (data.delete) {
          allSettingInstructionsForLinkedObject = allSettingInstructionsForLinkedObject.filter(
            (setting) => setting.id !== data.id
          )
        } else {
          const newId = generateUniqueId(
            allSettingInstructionsForLinkedObject.length,
            settingsAndTimeSeriesSettingsInstructionsForLinkedObject
          )
          const newInstruction = {
            level: settingsData.level || '',
            name: settingsData.name || '',
            attribute: settingsData.attribute || '',
            priority: settingsData.priority || 0,
            comment: settingsData.comment || '',
            start_time: settingsData.start_time || undefined,
            end_time: settingsData.end_time || undefined,
            value: settingsData.value as number,
            creator_name: settingsData.creator_name || '',
            updated_at: Datetime.getISONow(0, false),
            id: data.id ?? newId,
          }

          if (updatingSetting) {
            const indexOfUpdatedSetting = allSettingInstructionsForLinkedObject.findIndex(
              (setting) => setting.id === data.id
            )
            allSettingInstructionsForLinkedObject[indexOfUpdatedSetting] = newInstruction
          } else {
            allSettingInstructionsForLinkedObject.push(newInstruction)
          }
        }

        return {
          ...previousChangeRequestPost,
          setting_instructions: {
            linked_objects: (previousChangeRequestPost.setting_instructions?.linked_objects ?? []).map(
              (linkedObject) => {
                if (
                  linkedObject.level === settingsData.level &&
                  linkedObject.name === settingsData.name &&
                  linkedObject.attribute === settingsData.attribute
                ) {
                  return {
                    ...linkedObject,
                    setting_instructions: allSettingInstructionsForLinkedObject,
                  }
                }
                return linkedObject
              }
            ),
          },
        }
      }
    })
  }, [])

  return (
    <>
      <Dialog
        open={true}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            border: `2px solid ${colors.warning3}`,
          },
        }}
        onClose={(_, reason) => {
          if (reason === 'escapeKeyDown') {
            closeModal() //TODO: update this
          }
        }}
        slotProps={{ paper: { 'data-testid': 'event-change-request-dialog' } }}
      >
        <CustomDialogTitle
          title={changeRequestPost.name ?? ''}
          handleClose={
            modalMode === 'new' && hasChanges
              ? () => updateUiState({ confirmThrowAwayChangeRequest: true })
              : modalMode === 'edit' && hasChanges
                ? () => updateUiState({ confirmCloseChangeRequest: true })
                : modalMode === 'edit' && !hasChanges
                  ? () => setModalMode('read-only')
                  : closeModal
          }
          infoText={modalMode === 'edit' || modalMode === 'new' ? <EditModeBanner /> : ''}
        />

        <DialogContent>
          <div className={styles.EventPostModal_FormRow}>
            <TextField
              fullWidth
              margin="dense"
              label={t('Name event')}
              disabled={modalMode === 'read-only'}
              value={changeRequestPost.name ?? ''}
              onChange={(e) => {
                const newChangeRequestPost = {
                  ...changeRequestPost,
                  name: e.target.value as string,
                }
                setChangeRequestPost(newChangeRequestPost)
              }}
            />
            <Dropdown
              fullWidth
              margin="dense"
              label={t('Event type')}
              disabled={modalMode === 'read-only'}
              items={EVENT_TYPES.map((type) => ({ value: type.value, label: t(type.label) }))}
              value={changeRequestPost.operational_event_type ?? ''}
              handleChange={(e) => {
                const newChangeRequestPost = {
                  ...changeRequestPost,
                  operational_event_type: e.target.value as EventChangeRequest['operational_event_type'],
                }
                setChangeRequestPost(newChangeRequestPost)
              }}
            />
            <SearchableDropdown
              fullWidth
              margin="dense"
              label={t('Cause category')}
              disabled={modalMode === 'read-only'}
              items={
                Array.isArray(eventCategories)
                  ? eventCategories.map((type) => ({
                      value: type.id ?? '',
                      label: type.name ?? '',
                      subHeader: type.category_group?.name ?? '',
                    }))
                  : []
              }
              value={changeRequestPost.category_id ?? ''}
              handleChange={(value) => {
                const newChangeRequestPost = {
                  ...changeRequestPost,
                  category_id: value as number,
                }
                setChangeRequestPost(newChangeRequestPost)
              }}
            />
          </div>
          <div className={styles.EventPostModal_FormRow}>
            <div className={styles.EventPostModal_DatePickerPeriod}>
              <div>
                <DatePickerPeriod
                  isDisabled={modalMode === 'read-only'}
                  startDate={changeRequestPost.start_time}
                  endDate={changeRequestPost.end_time}
                  onSelect={(_startTime, _endTime) => {
                    const startTime = _startTime
                    const endTime = _endTime
                    const newStartTime = startTime
                      ? Datetime.toISOString(startTime)
                      : Datetime.getStartOfDay(Datetime.getISONow())
                    const newEndTime = endTime
                      ? Datetime.toISOString(endTime)
                      : Datetime.getEndOfDay(Datetime.getISONow())
                    const newChangeRequestPost = {
                      ...changeRequestPost,
                      start_time: newStartTime,
                      end_time: newEndTime,
                    }
                    setChangeRequestPost(newChangeRequestPost)
                  }}
                  // onError={setHasInvalidDates} Add in helper file add import
                  minDate={
                    changeRequestPost.earliest_start_time
                      ? Datetime.toISOString(changeRequestPost.earliest_start_time)
                      : Datetime.toISOString('2000-01-01T00:00:00+00:00')
                  }
                  maxDate={
                    changeRequestPost.latest_end_time
                      ? Datetime.toISOString(changeRequestPost.latest_end_time)
                      : Datetime.toISOString('2100-01-01T00:00:00+00:00')
                  }
                />
              </div>
            </div>
            <div className={styles.EventPostModal_Schedulability}>
              <Dropdown
                fullWidth
                margin="dense"
                label={t('Schedulability')}
                disabled={modalMode === 'read-only'}
                items={SCHEDULABILITIES.map((type) => ({ value: type.value, label: t(type.label) }))}
                value={changeRequestPost.schedulability ?? ''}
                handleChange={(e) => {
                  const newChangeRequestPost = getEventPostForSchedulabilityChange(
                    changeRequestPost,
                    e.target.value as EventChangeRequest['schedulability']
                  )
                  setChangeRequestPost(newChangeRequestPost)
                }}
              />
            </div>
          </div>
          {(changeRequestPost.schedulability === 'MoveableWithinLimits' ||
            changeRequestPost.schedulability === 'AlwaysMoveable') && (
            <>
              <div className={styles.EventPostModal_FormRow}>
                {changeRequestPost.schedulability === 'MoveableWithinLimits' && (
                  <div className={styles.EventPostModal_DatePickerPeriod_FormRow}>
                    <DatePickerPeriod
                      isDisabled={modalMode === 'read-only'}
                      startDate={
                        changeRequestPost.earliest_start_time
                          ? changeRequestPost.earliest_start_time
                          : Datetime.toISOString(Datetime.subtractHours(changeRequestPost.start_time, 24))
                      }
                      endDate={
                        changeRequestPost.latest_end_time
                          ? changeRequestPost.latest_end_time
                          : Datetime.toISOString(Datetime.addHours(changeRequestPost.end_time, 24))
                      }
                      onSelect={(startTime, endTime) => {
                        const newStartTime = startTime
                          ? Datetime.toISOString(startTime)
                          : Datetime.toISOString(Datetime.subtractHours(changeRequestPost.start_time, 24))
                        const newEndTime = endTime
                          ? Datetime.toISOString(endTime)
                          : Datetime.toISOString(Datetime.addHours(changeRequestPost.end_time, 24))
                        const newChangeRequestPost = {
                          ...changeRequestPost,
                          earliest_start_time: newStartTime,
                          latest_end_time: newEndTime,
                        }
                        setChangeRequestPost(newChangeRequestPost)
                      }}
                      // onError={setHasInvalidDateLimit}
                      startLabel={t('Starts earliest')}
                      endLabel={t('Ends latest')}
                    />
                  </div>
                )}
                <div className={styles.EventPostModal_Consequence}>
                  <TextField
                    fullWidth
                    margin="dense"
                    label={
                      t('Consequence of time adjustment') +
                      (uiState.schedulabilityEffectLength > 0
                        ? ` (${uiState.schedulabilityEffectLength}/${MAX_SCHEDULABILITY_EFFECT_INPUT_LENGTH})`
                        : '')
                    }
                    disabled={modalMode === 'read-only'}
                    value={changeRequestPost.schedulability_effect}
                    onChange={(e) => {
                      updateUiState({ schedulabilityEffectLength: e.target.value.length })
                      const newChangeRequestPost = {
                        ...changeRequestPost,
                        schedulability_effect: e.target.value,
                      }
                      setChangeRequestPost(newChangeRequestPost)
                    }}
                    slotProps={{
                      htmlInput: { maxLength: MAX_SCHEDULABILITY_EFFECT_INPUT_LENGTH },
                    }}
                  />
                </div>
              </div>
            </>
          )}
          {hasSettingsOutsideTimePeriod(settingInstructions, changeRequestPost) && (
            <div className={`${styles.EventPostModal_FormRow}`} style={{ marginBottom: '10px' }}>
              <InfoBanner
                text={t(`There are settings in this event outside the event time period`)}
                style={'warning'}
              />
            </div>
          )}
          <div className={styles.EventPostModal_FourthRow}>
            {isQuillMounted ? (
              <ReactQuill
                ref={quillRef}
                modules={reactQuillModules}
                value={inputFieldEditorContent}
                readOnly={disableQuillEditor}
                className={classNames(styles.EventPostModal_QuillEditor, {
                  [styles.EventPostModal_QuillEditor_Disabled]: disableQuillEditor,
                })}
                //delta and source, even though unused in the function, are required by Quills internal state management
                onChange={(content, _delta, source, editor) => handleEditorChange(content, source, editor)}
              />
            ) : (
              <div className={styles.EventPostModal_QuillEditor} />
            )}
          </div>
          {uiState.editorErrorMessage && (
            <div className={styles.EventPostModal_EditorErrorMessage}>{t(uiState.editorErrorMessage)}</div>
          )}
          <div className={styles.EventPostModal_FormRow}>
            {hasAccessToUMMCheckbox && (
              <FormGroup>
                <FormControlLabel
                  label={t('Requires UMM')}
                  disabled={modalMode === 'read-only'}
                  className={
                    changeRequestPost.require_umm
                      ? styles.EventPostModal_CheckboxYellowBackground
                      : styles.EventPostModal_Checkbox
                  }
                  control={
                    <MuiCheckbox
                      checked={changeRequestPost.require_umm}
                      onChange={() => {
                        const newChangeRequestPost = {
                          ...changeRequestPost,
                          require_umm: !changeRequestPost.require_umm,
                        }
                        setChangeRequestPost(newChangeRequestPost)
                      }}
                    />
                  }
                />
              </FormGroup>
            )}

            <FormGroup>
              <FormControlLabel
                label={t('Cancelable after start')}
                disabled={modalMode === 'read-only'}
                className={styles.EventPostModal_Checkbox}
                control={
                  <MuiCheckbox
                    checked={!!changeRequestPost.cancelable_after_start}
                    onChange={() => {
                      const newChangeRequestPost = {
                        ...changeRequestPost,
                        cancelable_after_start: !changeRequestPost.cancelable_after_start,
                      }
                      setChangeRequestPost(newChangeRequestPost)
                    }}
                  />
                }
              />
            </FormGroup>
          </div>
          <Divider className={styles.EventPostModal_Divider} />
          <Accordion
            disableGutters
            elevation={0}
            defaultExpanded={true}
            sx={{
              '&:before': {
                // Disable separator above accordion in collapsed mode
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<MuiIcon fontSize="small" className={`fas fa-chevron-down`} />}
              sx={{
                '&.MuiAccordionSummary-root': {
                  padding: 0,
                },
              }}
            >
              <span>{t('Affected components')}</span>
            </AccordionSummary>
            {hasSettingsOutsideTimePeriod(settingInstructions, changeRequestPost as EventChangeRequest) && (
              <div className={styles.EventPostModal_FormRow} style={{ marginBottom: '15px' }}>
                <InfoBanner
                  text={t(`There are settings in this event outside the event time period`)}
                  style={'warning'}
                />
              </div>
            )}

            {!linkedObjectFromInstructions ? (
              <EmptyElement />
            ) : (
              linkedObjectFromInstructions?.map((linkedObject, index) => (
                <div
                  key={`${linkedObject.level}-${linkedObject.name}-${normalizeLinkedObjectAttribute(linkedObject.attribute)}`}
                >
                  <div className={styles.EventPostModal_AlignCenter}>
                    <Tooltip
                      title={
                        getNumberOfSettingInstructions(settingInstructions, changeRequestPost, index) > 0
                          ? t(
                              `Affected component can't be changed when settings are connected. Remove settings to change these values or add a new affected component.`
                            )
                          : ''
                      }
                      arrow
                      placement="top"
                    >
                      <div className={styles.EventPostModal_FormRow}>
                        <EventPropertiesDropdownsSettingInstructions
                          eventPost={changeRequestPost}
                          setEventPost={(event: EventPost | EventChangeRequest) =>
                            setChangeRequestPost({
                              ...(event as EventChangeRequest),
                              setting_instructions: (event as EventChangeRequest).setting_instructions ?? {
                                linked_objects: [],
                              },
                            })
                          }
                          linkedObject={{
                            level: linkedObject.level,
                            name: linkedObject.name,
                            attribute: linkedObject.attribute,
                          }}
                          error={disabledSameLinkedObjects}
                          index={index}
                          disabled={
                            getNumberOfSettingInstructions(settingInstructions, changeRequestPost, index) > 0 ||
                            modalMode === 'read-only'
                          }
                        />
                      </div>
                    </Tooltip>
                    {linkedObjectFromInstructions && (
                      <>
                        <div className={styles.EventPostModal_FormRow_ActionButtons}>
                          <Icon
                            icon="fas fa-times-circle"
                            color="red"
                            large
                            disabled={
                              modalMode === 'read-only' ||
                              noSettingWritePermissionSettingInstructions(changeRequestPost, index)
                            }
                            onClick={() => {
                              updateUiState({
                                confirmOnRemoveAffectedComponent: true,
                                selectedAffectedComponent: index,
                              })
                            }}
                            tooltip={{
                              title: noSettingWritePermissionSettingInstructions(changeRequestPost, index)
                                ? t('You do not have permission to remove an affected component')
                                : t('Remove affected component'),
                              placement: 'top',
                              arrow: true,
                            }}
                          />
                          <Icon
                            icon="fas fa-cog"
                            color={colors.grey6}
                            large
                            disabledTooltip={t('Select a property to add settings')}
                            onClick={() => toggleSettingOverview(index)}
                            tooltip={{
                              title: modalMode === 'read-only' ? t('View settings') : t('View/add settings'),
                              placement: 'top',
                              arrow: true,
                            }}
                          />
                          {getNumberOfSettingInstructions(settingInstructions, changeRequestPost, index) > 0 && (
                            <Tooltip
                              title={`${getNumberOfSettingInstructions(settingInstructions, changeRequestPost, index)} ${t('Settings')}`}
                              arrow
                              placement="top"
                            >
                              <div className={styles.EventPostModal_YellowCircle} />
                            </Tooltip>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {(uiState.openSettingsOverview[index] ?? true) && (
                    <ChangeRequestSettingsInstructionsOverviewTable
                      index={index}
                      changeRequest={changeRequestPost}
                      level={linkedObjectFromInstructions?.[index]?.level || ''}
                      name={linkedObjectFromInstructions?.[index]?.name || ''}
                      attribute={linkedObjectFromInstructions?.[index]?.attribute || ''}
                      settings={settingInstructions.filter((setting) => {
                        return (
                          setting.name === linkedObjectFromInstructions?.[index]?.name &&
                          setting.attribute === linkedObjectFromInstructions?.[index]?.attribute &&
                          setting.level === linkedObjectFromInstructions?.[index]?.level
                        )
                      })}
                      disabled={modalMode === 'read-only'}
                      setSettingInstructionsData={updateSettingInstructions}
                    />
                  )}
                </div>
              ))
            )}
            {modalMode === 'read-only' ? (
              <EmptyElement />
            ) : (
              <div className={styles.EventPostModal_AddProperty}>
                <Button link icon="fal fa-plus" onClick={addEventPropertyField}>
                  {t('Add')}
                </Button>
                {disabledSameLinkedObjects && (
                  <InfoBanner text={t(`You can't have two of the same affected components`)} style={'danger'} />
                )}
              </div>
            )}
          </Accordion>
          <Divider className={styles.EventPostModal_Divider} />
          {modalMode === 'read-only' && (
            <div className={styles.EventPostModal_EventInformation}>
              <p style={{ fontStyle: 'italic' }}>{createdAndUpdatedInfo}</p>
              {/* Add other info later when request is sent */}
            </div>
          )}
          <div className="mt-4">
            <EventChangeRequestActionButtons
              modalMode={modalMode}
              hasChanges={hasChanges}
              hasEmptyFields={hasEmptyFields}
              onCancel={() => {
                if (modalMode === 'new' && hasChanges) {
                  updateUiState({ confirmThrowAwayChangeRequest: true })
                } else if (modalMode === 'edit' && hasChanges) {
                  updateUiState({ confirmCloseChangeRequest: true })
                } else if (modalMode === 'edit' && !hasChanges) {
                  setModalMode('read-only')
                } else {
                  closeModal()
                }
              }}
              onOpenDeleteOwn={() => updateUiState({ confirmDeleteOwnChangeRequest: true })}
              onOpenComment={() => updateUiState({ showCommentModal: true })}
              onEdit={() => setModalMode('edit')}
              onOpenReject={() => updateUiState({ confirmRejectChangeRequest: true })}
              onOpenApprove={() => updateUiState({ confirmApproveChangeRequest: true })}
              onOpenSubmit={() => updateUiState({ handInChangeRequest: true })}
              permittedTo={permittedTo}
            />
          </div>
        </DialogContent>
      </Dialog>

      <EventChangeRequestDialogs
        uiState={uiState}
        modalMode={modalMode}
        changeRequestPost={changeRequestPost}
        eventPost={eventPost}
        updateUiState={updateUiState}
        closeModal={closeModal}
        setModalMode={setModalMode}
        sendChangeRequest={sendChangeRequest}
        updateChangeRequest={updateChangeRequest}
        deleteChangeRequest={deleteChangeRequest}
        rejectChangeRequest={rejectChangeRequest}
        approveChangeRequest={approveChangeRequest}
        removeEventPropertyField={removeEventPropertyField}
      />
    </>
  )
}

type EventChangeRequestActionButtonsProps = {
  modalMode: 'new' | 'edit' | 'read-only'
  hasChanges: boolean
  hasEmptyFields: boolean
  onCancel: () => void
  onOpenDeleteOwn: () => void
  onOpenComment: () => void
  onEdit: () => void
  onOpenReject: () => void
  onOpenApprove: () => void
  onOpenSubmit: () => void
  permittedTo: (action: PermittedActionsChangeRequest) => boolean
}

function EventChangeRequestActionButtons({
  modalMode,
  hasChanges,
  hasEmptyFields,
  onCancel,
  onOpenDeleteOwn,
  onOpenComment,
  onEdit,
  onOpenReject,
  onOpenApprove,
  onOpenSubmit,
  permittedTo,
}: EventChangeRequestActionButtonsProps): ReactElement {
  const { t } = useTranslation()

  return (
    <div className="flex items-center justify-between gap-1">
      <div className="flex gap-1">
        <Button secondary onClick={onCancel}>
          {t('Cancel')}
        </Button>
        {modalMode === 'read-only' && permittedTo('delete_change_request') && (
          <Button secondary danger onClick={onOpenDeleteOwn}>
            {t('Throw away my change request')}
          </Button>
        )}
      </div>
      <div>
        {modalMode === 'read-only' && (
          <div className="flex gap-1">
            <Button secondary onClick={onOpenComment}>
              {t('View comment')}
            </Button>
            <Tooltip
              title={!permittedTo('update') ? t('You do not have permission to update change request') : ''}
              arrow
            >
              <span>
                <Button primary disabled={!permittedTo('update')} onClick={onEdit}>
                  {t('Edit change request')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={!permittedTo('reject') ? t('You do not have permission to reject this change request') : ''}
              arrow
            >
              <span>
                <Button primary disabled={!permittedTo('reject')} onClick={onOpenReject} danger>
                  {t('Reject')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={!permittedTo('approve') ? t('You do not have permission to approve this change request') : ''}
              arrow
            >
              <span>
                <Button
                  primary
                  disabled={!permittedTo('approve')}
                  onClick={onOpenApprove}
                  style={permittedTo('approve') ? { backgroundColor: colors.success4 } : {}}
                >
                  {t('Approve')}
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
        {(modalMode === 'new' || modalMode === 'edit') && (
          <Button
            primary
            disabled={!hasChanges || hasEmptyFields}
            disabledTooltip={
              hasEmptyFields
                ? t('Some fields are empty, fill them in to be able to submit')
                : !hasChanges
                  ? t('No changes to save')
                  : ''
            }
            onClick={onOpenSubmit}
          >
            {modalMode === 'new' ? t('Submit with comment') : t('Update with comment')}
          </Button>
        )}
      </div>
    </div>
  )
}

type EventChangeRequestDialogsProps = {
  uiState: EventChangeRequestUiState
  modalMode: 'new' | 'edit' | 'read-only'
  changeRequestPost: EventChangeRequest
  eventPost: EventPost
  updateUiState: (update: Partial<EventChangeRequestUiState>) => void
  closeModal: () => void
  setModalMode: (value: 'new' | 'edit' | 'read-only' | null) => void
  sendChangeRequest: (data: EventChangeRequest, event_id: number) => Promise<void>
  updateChangeRequest: (data: EventChangeRequest, event_id: number, change_request_id: number) => Promise<void>
  deleteChangeRequest: (event_id: number, change_request_id: number) => Promise<void>
  rejectChangeRequest: (event_id: number, change_request_id: number) => Promise<void>
  approveChangeRequest: (event_id: number, change_request_id: number) => Promise<void>
  removeEventPropertyField: (index: number) => void
}

function EventChangeRequestDialogs({
  uiState,
  modalMode,
  changeRequestPost,
  eventPost,
  updateUiState,
  closeModal,
  setModalMode,
  sendChangeRequest,
  updateChangeRequest,
  deleteChangeRequest,
  rejectChangeRequest,
  approveChangeRequest,
  removeEventPropertyField,
}: EventChangeRequestDialogsProps): ReactElement {
  const { t } = useTranslation()

  return (
    <>
      <Dialog
        open={uiState.handInChangeRequest}
        onClose={() => updateUiState({ handInChangeRequest: false })}
        maxWidth="md"
        fullWidth
        aria-modal
        slotProps={{ paper: { 'data-testid': 'change-request-reason-dialog' } }}
      >
        <CustomDialogTitle
          title={t('Enter reason for change request')}
          handleClose={() => updateUiState({ handInChangeRequest: false })}
        />
        <DialogContent>
          <TextField
            fullWidth
            type="text"
            margin="normal"
            label={t('Enter reason for change request')}
            multiline
            minRows={3}
            variant="outlined"
            value={uiState.changeRequestComment}
            onChange={(e) => updateUiState({ changeRequestComment: e.target.value })}
          />
          <div className="flex justify-between mt-4">
            <Button secondary onClick={() => updateUiState({ handInChangeRequest: false })}>
              {t('Cancel')}
            </Button>
            <Button
              primary
              disabled={uiState.changeRequestComment === ''}
              onClick={() => {
                const comment = Array.isArray(uiState.changeRequestComment)
                  ? uiState.changeRequestComment[0]
                  : uiState.changeRequestComment
                if (modalMode === 'new') {
                  sendChangeRequest({ ...changeRequestPost, comment }, eventPost.id as number)
                } else {
                  updateChangeRequest(
                    { ...changeRequestPost, comment },
                    eventPost.id as number,
                    eventPost.pending_change_request_id as number
                  )
                }
              }}
            >
              {modalMode === 'new' ? t('Submit change request') : t('Update change request')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={uiState.showCommentModal}
        onClose={() => updateUiState({ showCommentModal: false })}
        maxWidth="md"
        fullWidth
        aria-modal
        slotProps={{ paper: { 'data-testid': 'change-request-comment-dialog' } }}
      >
        <CustomDialogTitle
          title={t('Comment for change request')}
          handleClose={() => updateUiState({ showCommentModal: false })}
        />
        <DialogContent>
          <div className="flex flex-col gap-4">
            <span>{uiState.changeRequestComment}</span>
          </div>
          <div className="flex justify-start mt-4">
            <Button secondary onClick={() => updateUiState({ showCommentModal: false })}>
              {t('Close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={uiState.confirmCloseChangeRequest}
        onConfirm={() => {
          updateUiState({ confirmCloseChangeRequest: false })
          setModalMode('read-only')
        }}
        onClose={() => updateUiState({ confirmCloseChangeRequest: false })}
        textObject={{
          title: t(`Are you sure you want to close this window?`),
          text: t('All your progress will be lost if you continue.'),
          confirm: t('Yes'),
        }}
      />
      <ConfirmDialog
        open={uiState.confirmThrowAwayChangeRequest}
        onConfirm={() => {
          updateUiState({ confirmThrowAwayChangeRequest: false })
          closeModal()
        }}
        onClose={() => updateUiState({ confirmThrowAwayChangeRequest: false })}
        textObject={{
          title: t(`Are you sure you want to throw away this change request?`),
          text: t('All your progress will be lost if you continue.'),
          confirm: t('Yes'),
        }}
      />
      <ConfirmDialog
        open={uiState.confirmDeleteOwnChangeRequest}
        onConfirm={() => {
          if (eventPost.id && eventPost.pending_change_request_id) {
            deleteChangeRequest(eventPost.id, eventPost.pending_change_request_id)
            updateUiState({ confirmDeleteOwnChangeRequest: false })
            closeModal()
          }
        }}
        onClose={() => updateUiState({ confirmDeleteOwnChangeRequest: false })}
        textObject={{
          title: t(`Are you sure you want to throw away this change request?`),
          text: t('The change request will be deleted and all your progress will be lost if you continue.'),
          confirm: t('Yes'),
        }}
      />
      <ConfirmDialog
        open={uiState.confirmRejectChangeRequest}
        onConfirm={() => {
          if (eventPost.id && eventPost.pending_change_request_id) {
            rejectChangeRequest(eventPost.id, eventPost.pending_change_request_id as number)
            updateUiState({ confirmRejectChangeRequest: false })
            closeModal()
          }
        }}
        onClose={() => updateUiState({ confirmRejectChangeRequest: false })}
        textObject={{
          title: t(`Are you sure you want to reject this change request?`),
          text: t('The change request will be rejected and none of the changes will be applied.'),
          confirm: t('Yes'),
        }}
      />
      <ConfirmDialog
        open={uiState.confirmApproveChangeRequest}
        onConfirm={() => {
          if (eventPost.id && eventPost.pending_change_request_id) {
            approveChangeRequest(eventPost.id, eventPost.pending_change_request_id)
            updateUiState({ confirmApproveChangeRequest: false })
            closeModal()
          }
        }}
        onClose={() => updateUiState({ confirmApproveChangeRequest: false })}
        textObject={{
          title: t(`Are you sure you want to approve this change request?`),
          text: t('The change request will be approved and the changes will be applied to the event.'),
          confirm: t('Yes'),
        }}
      />
      <ConfirmDialog
        open={uiState.confirmOnRemoveAffectedComponent}
        onConfirm={() => {
          removeEventPropertyField(uiState.selectedAffectedComponent)
          updateUiState({ confirmOnRemoveAffectedComponent: false, selectedAffectedComponent: -1 })
        }}
        onClose={() => updateUiState({ confirmOnRemoveAffectedComponent: false, selectedAffectedComponent: -1 })}
        textObject={{
          title: t(`Are you sure you want to remove this affected component?`),
          text: t('If you remove this affected component, all settings connected to it will be removed as well.'),
          confirm: t('Yes, remove'),
        }}
      />
    </>
  )
}

const EditModeBanner = React.memo(function EditModeBanner(): ReactElement {
  const { t } = useTranslation()
  return (
    <div className="p-2 w-100 flex justify-center items-center border border-warning-4 bg-warning-3 rounded">
      <span className="font-semibold">{t('Edit mode')}</span>
    </div>
  )
})
